import * as PIXI from 'pixi.js';
import { int2rgb } from '../../libs/videoData';
import { Config } from '../../video-data/config';
import { TextEditingManager } from './TextEditingManager';
import type { TextLineMetrics } from './TextLineMetrics';
import type { WordElementSprite } from './WordElementSprite';
import type { TextLine } from './TextLine';
import { getRootContainerScale } from './TextEditingUtilities';

/**
 * This file contains all methods needed to generate DOM nodes for the Natural Text Editing input
 *
 * !! NOTE !! it is assumed that getBounds() has been called on the parent text asset before calling
 *      any of these methods, i.e. the bounds are up to date.
 */


/**
 * Creates the text editing input with all static property values, static styling,
 * and events listeners attached
 */
export function createTextInput(): HTMLDivElement {
    const textAssetNode = document.createElement('div');
    textAssetNode.contentEditable = 'true';
    textAssetNode.style.color = 'transparent';
    textAssetNode.style.fontFamily = 'inherit';
    textAssetNode.style.width = '100%';
    textAssetNode.style.height = '100%';
    textAssetNode.spellcheck = false;
    textAssetNode.id = TextEditingManager.contentEditableId;
    return textAssetNode;
}

/**
 * Create an HTML node that has non-zero bounds, styled like the text the user
 * will see once that start typing
 *
 * @param caretColor CSS caretColor value
 */
export function createTextPlaceholder(caretColor: string): HTMLDivElement {
    const placeholderNode = document.createElement('div');
    placeholderNode.id = TextEditingManager.placeholderId;
    placeholderNode.style.caretColor = caretColor;
    placeholderNode.style.height = '100%';
    placeholderNode.style.width = '100%';
    return placeholderNode;
}

/**
 * Creates nodes for all text lines and wordElements of a given textAsset
 */
export function buildInputContent(textEditingManager: TextEditingManager): Array<HTMLDivElement> {
    const textAsset = textEditingManager.textAsset;
    const textLineList = textAsset.textLinesForEditing;
    const textLineNodes = [];

    for (let i = 0; i < textLineList.length; i++) {
        const textLine = textLineList[i];
        const textLineMetrics = textLineList[i].metrics;
        if (isEmptyLine(textLineMetrics)) {
            const startIdx = i;
            while (
                i + 1 < textAsset.textMetrics.lines.length
                && isEmptyLine(textAsset.textMetrics.lines[i + 1])
            ) {
                i++;
            }
            textLineNodes.push(
                ...createEmptyTextLines(textEditingManager, startIdx, i)
            );
        }
        else {
            textLineNodes.push(
                createNonEmptyTextLine(textLine, textEditingManager)
            );
        }
    }

    return textLineNodes;
}

/**
 * Constructs an absolutely positioned block container representing a text line that contains
 * children for each of its wordElements
 */
function createGenericLine(): HTMLDivElement {
    const lineNode = document.createElement('div');
    lineNode.classList.add(TextEditingManager.textLineClass);
    lineNode.style.position = 'absolute';
    lineNode.style.fontFamily = 'inherit';
    return lineNode;
}

/**
 * Creates a container for a text line with at least one glyph. Populates the container
 * with all relevant wordElement nodes.
 */
function createNonEmptyTextLine(
    textLine: TextLine,
    textEditingManager: TextEditingManager
): HTMLDivElement {
    const textAsset = textEditingManager.textAsset;
    const lineNode = createGenericLine();
    const textLineMetrics = textLine.metrics;
    const lineBounds = textLine.getBounds(true);
    const textAssetBounds = textAsset.getBounds(true);
    const wordElementList = textLine.wordElementSprites;
    const height = lineBounds.height;
    const offsetY = lineBounds.y - textAssetBounds.y;

    // set height styling
    const resize = (canvasWidth: number) => {
        if (canvasWidth === 0) {
            // Canvas is not part of DOM
            return;
        }
        // Update font styles
        const domScale = getRootContainerScale(textAsset.root, canvasWidth);
        // set height styling
        lineNode.style.lineHeight = `${domScale * height}px`;
        lineNode.style.height = `${domScale * height}px`;
        lineNode.style.top = `${domScale * offsetY}px`;
        const fontSize: number = domScale * textAsset.normalStyle.fontSize;
        lineNode.style.fontSize = `${fontSize}px`;
    };
    resize(textEditingManager.domContainer.clientWidth);
    textEditingManager._addOnResizeCallback(resize);

    const isRTL = textAsset.readingDirection === Config.READING_DIRECTION.RTL;
    lineNode.style.width = '100%';

    let offset;
    if (!isRTL) {
        const firstWordEleIdx = textLineMetrics.removedLeadingSpace ? 1 : 0;
        const firstWordBounds = wordElementList[firstWordEleIdx].getBounds();
        offset = (firstWordBounds.x - textAssetBounds.x) / textAssetBounds.width;
    }
    else {
        const lastWordEleIdx = textLineMetrics.removedTrailingSpace ?
            wordElementList.length - 1 : wordElementList.length - 1;
        const lastWordEleBounds = wordElementList[lastWordEleIdx].getBounds();
        offset = (lastWordEleBounds.x - textAssetBounds.x) / textAssetBounds.width;
    }

    // Create a node for each wordElement
    wordElementList.forEach((wordElement, i) => {
        const caretColor = int2rgb(wordElement.metrics.style.fill);
        const isBreakingSpace = PIXI.TextMetrics.isBreakingSpace(wordElement.text);

        const isLeadingWhitespace = i === 0
            && textLineMetrics.removedLeadingSpace
            && isBreakingSpace;
        const isTrailingWhitespace = i === wordElementList.length - 1
            && textLineMetrics.removedTrailingSpace
            && isBreakingSpace;

        let wordEleNode = null;
        if (isLeadingWhitespace || isTrailingWhitespace) {
            wordEleNode = createResetWordElement(caretColor, wordElement.text);
        }
        else {
            wordEleNode = createWordElement(textEditingManager, wordElementList, i);
        }

        const isFirstNodeAndLTR = !isRTL && i === 0;
        const isLastNodeAndRTL = isRTL && i === wordElementList.length - 1;
        if (isFirstNodeAndLTR || isLastNodeAndRTL) {
            wordEleNode.style.marginLeft = `${offset * 100}%`;
        }

        lineNode.appendChild(wordEleNode);
    });

    return lineNode;
}

/**
 * Creates a series of divs to represent sequential empty lines.
 *
 * Empty lines are always created together since individual metric instances for
 * empty lines may not contain the correct bounds information that will translate
 * nicely to nodes living in the contenteditable.
 *
 * @param startIdx index of the first empty line relative to all text line metrics
 * @param endIdx index of the last empty line relative to all text line metrics
 */
function createEmptyTextLines(
    textEditingManager: TextEditingManager,
    startIdx: number,
    endIdx: number
): Array<HTMLDivElement> {
    const textAsset = textEditingManager.textAsset;
    const textAssetBounds = textAsset.getBounds(true);
    const lineMetricList = textAsset.textMetrics.lines;

    // The height of each line is the cumulative height of the last non-empty line and the next
    // non empty line, divided by the number of empty lines
    const numEmptyLines = endIdx - startIdx + 1;

    let startY = 0;
    if (startIdx > 0) {
        const prevLine = lineMetricList[startIdx - 1];
        const prevLineBounds = prevLine.getBounds();
        startY = prevLineBounds.y + prevLineBounds.height;
    }

    const endYIdx = endIdx === lineMetricList.length - 1
        ? endIdx
        : endIdx + 1;
    const endY = lineMetricList[endYIdx].getBounds().y;

    const avgHeight = (endY - startY) / numEmptyLines;
    const avgLineHeight = avgHeight / textAsset.normalStyle.fontSize;

    // The horizontal offset, dependent on alignment, is set to match its neighbour
    const nonNewlineLineIdx = startIdx > 0 ? startIdx : endIdx + 1;
    const nonNewlineWordEleList = textAsset.textLinesForEditing[nonNewlineLineIdx].wordElementSprites;
    const firstWordEleBounds = nonNewlineWordEleList[0].getBounds();
    const lastWordEleBounds = nonNewlineWordEleList[nonNewlineWordEleList.length - 1].getBounds();
    const leftOffset = (firstWordEleBounds.x - textAssetBounds.x) / textAssetBounds.width;
    const rightOffset = (textAssetBounds.x
        + textAssetBounds.width
        - lastWordEleBounds.x
        - lastWordEleBounds.width
    ) / textAssetBounds.width;

    // Create nodes and apply styling
    const emptyLineNodeList = [];
    let currY = startY;
    for (let i = startIdx; i <= endIdx; i++) {
        const textLineMetrics = lineMetricList[i];
        const emptyLineNode = createGenericLine();
        emptyLineNode.style.lineHeight = `${avgLineHeight * 100}%`;
        emptyLineNode.style.height = `${avgHeight / textAssetBounds.height * 100}%`;
        emptyLineNode.style.width = '100%';
        emptyLineNode.style.top = `${currY / textAssetBounds.height * 100}%`;
        currY += avgHeight;

        const textAlign = textLineMetrics.style.align;
        emptyLineNode.style.textAlign = textAlign;
        if (textAlign === 'left') {
            emptyLineNode.style.paddingLeft = `${leftOffset * 100}%`;
        }
        else if (textAlign === 'right') {
            emptyLineNode.style.paddingRight = `${rightOffset * 100}%`;
        }

        const wordEleNode = document.createElement('div');
        wordEleNode.appendChild(document.createElement('br'));
        wordEleNode.dataset.text = '\n';
        wordEleNode.classList.add(TextEditingManager.wordElementClass);
        wordEleNode.style.width = '100%';
        wordEleNode.style.height = '100%';
        wordEleNode.style.caretColor = int2rgb(textLineMetrics.style.fill);
        emptyLineNode.appendChild(wordEleNode);

        emptyLineNodeList.push(emptyLineNode);
    }

    return emptyLineNodeList;
}

/**
 * Generates a special node for a leading breaking space that had its bounds reset
 *
 * @param caretColor cssString
 */
function createResetWordElement(caretColor: string, dataValue: string): HTMLSpanElement {
    const wordEleNode = createGenericWordElement(caretColor);
    wordEleNode.textContent = '\u200B';
    wordEleNode.dataset.text = dataValue;
    wordEleNode.style.width = '0';
    return wordEleNode;
}

/**
 * Factory method for a node representing a word element
 *
 * @param  caretColor CSS string
 */
function createGenericWordElement(caretColor: string): HTMLSpanElement {
    const wordEleNode = document.createElement('span');
    wordEleNode.classList.add(TextEditingManager.wordElementClass);
    wordEleNode.style.height = '100%';
    wordEleNode.style.display = 'inline-block';
    wordEleNode.style.verticalAlign = 'middle';
    wordEleNode.style.whiteSpace = 'pre';
    wordEleNode.style.caretColor = caretColor;
    return wordEleNode;
}

/**
 * Creates an inline HTML node representing a wordElement
 *
 * @param wordElementList a list of WordELement objects corresponding to the line the wordElement belongs in
 * @param idx the index of the wordElement contained in wordElementList to create a node for
 */
function createWordElement(
    textEditingManager: TextEditingManager,
    wordElementList: Array<WordElementSprite>,
    idx: number
): HTMLSpanElement {
    const textAsset = textEditingManager.textAsset;
    const currWordEle = wordElementList[idx];
    const wordEleNode = createGenericWordElement(int2rgb(currWordEle.metrics.style.fill));
    const textAssetBounds = textAsset.getBounds(true);
    const metrics = currWordEle.metrics;

    // Set the content and any styling based on the content
    if (currWordEle.text === '\n') {
        wordEleNode.dataset.text = '\n';
        wordEleNode.appendChild(document.createElement('br'));
    }
    else if (currWordEle.text === '\t') {
        wordEleNode.innerText = '\t';
        wordEleNode.style.tabSize = '1';
    }
    else {
        wordEleNode.innerText = currWordEle.text;
    }

    // Set a direction override if it's different than the root
    const isWhitespace = [' ', '\n', '\t'].some(char => currWordEle.text === char);
    if (!isWhitespace) {
        const globalTextDirection = textAsset.root.textDirection;
        const localTextDirection = metrics.direction;
        if (globalTextDirection !== localTextDirection) {
            wordEleNode.setAttribute('dir', localTextDirection);
        }
    }

    const token = metrics.wordElementToken;
    wordEleNode.style.fontFamily = token.font.familyName;

    // The highlighted font might have a different size. Check and override the inherited font size.
    if (token.highlighted) {
        wordEleNode.style.fontSize = `${metrics.style.fontSize}px`;
    }

    // determine the bounds of the current wordEle as well as the next sequential one
    const currBounds = currWordEle.getBounds();
    const isRTL = textAsset.readingDirection === Config.READING_DIRECTION.RTL;
    let nextEleBounds;
    if (!isRTL) {
        nextEleBounds = idx < wordElementList.length - 1
            ? wordElementList[idx + 1].getBounds()
            : null;
    }
    else {
        nextEleBounds = idx > 0
            ? wordElementList[idx - 1].getBounds()
            : null;
    }

    // When rendering highlighted text, animations can move the highlighted text to a different position.
    // since we place DOM nodes according to the normal style, we'll offset the values to make the nodes
    // align with the rendered text.
    const relativeOffset = { x: 0, y: 0 };

    if (token.highlighted && textAsset.highlightStyle.renderHighlighted) {
        const allHighlightSprites = textAsset._highlightLines.flatMap(line => line.wordElementSprites);
        // Find the corresponding token on the highlighted layer.
        // Highlighted tokens have already been split at this point, so there is only one token that will match.
        // We compare by reference as it's the same token object on the highlighted and normal layers.
        const highlightSprite = allHighlightSprites.find(s => s.metrics.wordElementToken === token);

        if (highlightSprite) {
            relativeOffset.x = highlightSprite.getBounds().x - currWordEle.getBounds().x;
            relativeOffset.y = highlightSprite.getBounds(true).y - currWordEle.getBounds(true).y;
        }
    }

    // The browser renders bearingX outside of the text bounding box.
    // Correct this difference by push dom element the other direction.
    relativeOffset.x -= metrics.bearingX;

    const resize = (canvasWidth: number) => {
        if (canvasWidth === 0) {
            // Canvas is not part of DOM
            return;
        }
        const domScale = getRootContainerScale(textAsset.root, canvasWidth);
        const positionX = domScale * relativeOffset.x;
        const positionY = domScale * relativeOffset.y;
        const fontSize = domScale * metrics.style.fontSize;
        const height = domScale * metrics.height;

        wordEleNode.style.lineHeight = `${height}px`;
        wordEleNode.style.height = `${height}px`;
        wordEleNode.style.fontSize = `${fontSize}px`;
        wordEleNode.style.position = 'relative';
        wordEleNode.style.left = `${positionX}px`;
        wordEleNode.style.top = `${positionY}px`;
    };
    resize(textEditingManager.domContainer.clientWidth);
    textEditingManager._addOnResizeCallback(resize);

    // determine the width of the node by using the difference between the x bounds of
    // the current wordEle and the next sequential one
    const currWidth = nextEleBounds
        ? nextEleBounds.x - currBounds.x
        : currBounds.width;
    wordEleNode.style.width = `${currWidth / textAssetBounds.width * 100}%`;

    return wordEleNode;
}

/**
 * Determines if the text line does not contain any sprites that have height and width
 */
function isEmptyLine(textLineMetrics: TextLineMetrics): boolean {
    const isJustNewline = textLineMetrics.text === '\n';
    const isJustStrippedWhitespace = textLineMetrics.text === ' '
        && textLineMetrics.removedLeadingSpace;

    return isJustNewline || isJustStrippedWhitespace;
}
